/**
 * 流入元の引き継ぎ。
 *
 * 広告 → この LP → LINE ミニアプリ → アプリ (app.babytale.net) という
 * 導線なので、広告のパラメータ (utm_* / fbclid) は LP の URL にしか無い。
 * ここで拾って LINE ミニアプリへのリンクに付け直さないと、アプリ側には
 * 「LP から来た」としか伝わらず、どの広告の成果か分からなくなる。
 *
 * Cookie は使えない。LP は通常のブラウザ、アプリは LINE アプリ内の
 * WebView で動き、別コンテキストなので共有されない。URL で運ぶしかない。
 *
 * テーマ設定のリンクには固定の utm_source=website&utm_medium=referral が
 * 入っている。これは「LP 経由で来た」という意味しかなく、広告から来た人の
 * 情報を上書きしてしまう。実際の流入元があるときはそちらを優先し、
 * 無いときだけ既定値として残す。
 *
 * 流入元は「最初に来たとき」のものを引き継ぐ (アプリは最初の流入元で
 * 集計する)。
 * - 着地した URL に utm やクリック ID があればそれを使う
 * - 無ければ参照元から作る。Google 検索なら utm_source=google&utm_medium=organic
 *   のように、アプリがそのまま「検索 (自然)」「SNS」「他サイト」に分類できる形にする
 * - LP の中でページを移ると URL のパラメータも参照元も消えるので、最初に
 *   着地したときの流入元を端末に残し (90 日。アプリの最初の流入元と同じ)、
 *   どのページのリンクにも付ける
 */
(function () {
  "use strict";

  /** 引き継ぐパラメータ。広告プラットフォームのクリック ID を含む */
  var FORWARD_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "fbclid",
    "gclid",
    "ttclid",
    "li_fat_id",
    "msclkid",
  ];

  /** 引き継ぎ先。ここに向かうリンクだけ書き換える */
  var TARGET_HOSTS = ["miniapp.line.me", "liff.line.me", "app.babytale.net"];

  /** 最初の流入元を残す場所と期間 */
  var STORAGE_KEY = "bt_lp_first_touch";
  var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

  /** 1 項目の最大長。異常に長い値でリンクを壊さないため */
  var MAX_LENGTH = 255;

  /** 参照元のドメインから流入の種類を決める (アプリの分類と揃える) */
  var SEARCH_HOSTS = ["google.", "yahoo.", "bing.com", "duckduckgo.com"];
  var SOCIAL_HOSTS = [
    "instagram.com",
    "facebook.com",
    "twitter.com",
    "x.com",
    "t.co",
    "tiktok.com",
    "youtube.com",
    "pinterest.com",
  ];

  function hostMatches(host, list, suffixOnly) {
    for (var i = 0; i < list.length; i++) {
      var h = list[i];
      if (suffixOnly ? host === h || host.endsWith("." + h) : host.indexOf(h) !== -1) {
        return true;
      }
    }
    return false;
  }

  /** utm・クリック ID のうち、値があるものだけを取り出す */
  function pickParams(searchParams) {
    var picked = {};
    var found = false;
    for (var i = 0; i < FORWARD_KEYS.length; i++) {
      var value = searchParams.get(FORWARD_KEYS[i]);
      if (value) {
        picked[FORWARD_KEYS[i]] = String(value).slice(0, MAX_LENGTH);
        found = true;
      }
    }
    return found ? picked : null;
  }

  /**
   * 参照元から utm を作る。自サイト (ストア内の移動・決済) や参照元なしは
   * null (分からないものは推測で埋めない)
   */
  function fromReferrer() {
    var ref = document.referrer;
    if (!ref) return null;
    var host;
    try {
      host = new URL(ref).hostname.toLowerCase().replace(/^www\./, "");
    } catch (e) {
      return null;
    }
    var self = window.location.hostname.toLowerCase().replace(/^www\./, "");
    if (!host || host === self || host.endsWith("." + self)) return null;
    if (host.endsWith("myshopify.com") || host.endsWith("shopify.com")) return null;

    var medium;
    var source = host;
    if (hostMatches(host, SEARCH_HOSTS, false)) {
      medium = "organic";
      source = host.split(".")[0] === "search" ? host.split(".")[1] : host.split(".")[0];
    } else if (hostMatches(host, SOCIAL_HOSTS, true)) {
      medium = "social";
      source = host.replace(/^(l|lm|m)\./, "").split(".")[0];
    } else if (host === "line.me" || host.endsWith(".line.me")) {
      medium = "line";
      source = "line";
    } else {
      medium = "referral";
    }
    return {
      utm_source: source.slice(0, MAX_LENGTH),
      utm_medium: medium,
    };
  }

  function loadFirstTouch() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.params || !saved.ts) return null;
      if (Date.now() - saved.ts > MAX_AGE_MS) return null;
      return saved;
    } catch (e) {
      return null;
    }
  }

  function saveFirstTouch(entry) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch (e) {
      /* 保存できない環境 (プライベートブラウズ等) では、このページの分だけ使う */
    }
  }

  /**
   * 引き継ぐ流入元を決める。
   *
   * 最初に来たときの流入元が残っていればそれを使う。無ければ今の着地から
   * 作り、残す。どちらも無ければ null (テーマ設定の既定値を残す)
   */
  function resolveAttribution() {
    var saved = loadFirstTouch();
    if (saved) return saved;

    var current = null;
    try {
      current = pickParams(new URL(window.location.href).searchParams);
    } catch (e) {
      current = null;
    }
    if (!current) current = fromReferrer();
    if (!current) return null;

    var entry = {
      params: current,
      landingPath: window.location.pathname.slice(0, MAX_LENGTH),
      ts: Date.now(),
    };
    saveFirstTouch(entry);
    return entry;
  }

  function isTargetLink(url) {
    for (var i = 0; i < TARGET_HOSTS.length; i++) {
      if (url.hostname === TARGET_HOSTS[i]) return true;
    }
    return false;
  }

  /**
   * リンクに流入元を書き写す。
   *
   * テーマ設定の固定値 (utm_source=website など) は、実際の流入元で
   * 置き換える。固定値の残りが混ざらないよう、utm は一度消してから付ける
   */
  function applyTo(anchor, attribution) {
    var url;
    try {
      url = new URL(anchor.getAttribute("href"), window.location.href);
    } catch (e) {
      return;
    }
    if (!isTargetLink(url)) return;

    var params = attribution.params;
    for (var i = 0; i < FORWARD_KEYS.length; i++) {
      var key = FORWARD_KEYS[i];
      if (params[key]) url.searchParams.set(key, params[key]);
      else if (key.indexOf("utm_") === 0) url.searchParams.delete(key);
    }
    // 最初に着地したページを残す。LP が複数ある場合の比較に使う
    url.searchParams.set("lp_path", attribution.landingPath);
    var next = url.toString();
    // 書き換え済みなら触らない (MutationObserver の無限ループを避ける)
    if (anchor.getAttribute("href") !== next) {
      anchor.setAttribute("href", next);
    }
  }

  var attribution = null;
  try {
    attribution = resolveAttribution();
  } catch (e) {
    attribution = null;
  }

  function run() {
    if (!attribution) return;
    var anchors = document.querySelectorAll("a[href]");
    for (var i = 0; i < anchors.length; i++) {
      applyTo(anchors[i], attribution);
    }
  }

  // 初回描画ぶん
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  // Shopify のセクションは後から差し込まれることがあるので、
  // 追加された分にも適用する。計測のための処理なので、
  // 失敗しても画面を壊さないよう全体を try で囲む
  try {
    var observer = new MutationObserver(function () {
      run();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  } catch (e) {
    /* MutationObserver が無い環境では初回ぶんだけで十分 */
  }
})();
